<template>
  <div v-if="shouldDisplayUpgrade">
    <button
      class="bg-static-ai-mid text-static-ai-hi shadow-long rounded-lg body-1-bold fixed left-1/2 z-[1] items-center hidden w-max -translate-x-1/2 p-12 max-w-[calc(100%-48px)]"
      :class="{
        'hover:bg-static-ai-mid-hover': canUpgrade,
        'cursor-default': isUpgraded,
        [isUpgradedLabelClassnames]: isUpgraded,
        [$style.animateShimmer]: isUpgraded,
        [$style.slideInFromBottom]: true,
      }"
      :style="pillStyle"
      @click="handlePillClick"
    >
      <div v-if="isUpgraded" class="flex items-center">
        <IconCheckWithSparkles v-if="canUpgrade || isUpgraded" class="mr-4" />
        <div>
          {{ i18n(translations.upgraded) }}
        </div>
      </div>
      <div v-else-if="canUpgrade" class="flex gap-8 items-center">
        <div
          class="flex h-40 w-40 shrink-0 items-center justify-center rounded-sm bg-surface-default-low p-4"
        >
          <RevIllustration
            v-if="pickers.upgrade"
            alt=""
            :height="32"
            :src="pickers.upgrade.image"
            image-class-names="h-full w-full object-contain leading-none"
            :width="32"
          />
        </div>
        <div class="flex flex-col items-start">
          <div class="caption">
            {{ i18n(translations.title) }}
          </div>
          <div class="text-static-default-hi body-1-bold text-left">
            {{ i18n(translations.canUpgrade) }}
          </div>
        </div>
      </div>
      <IconCrossSmall
        class="ml-8 md:ml-56 cursor-pointer text-static-default-hi shrink-0"
        @click="handleClose"
      />
    </button>
    <UpgradeModal :pickers @upgrade-accepted="handleUpgradeAccepted" />
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRoute, useState } from '#imports'
import { computed, onBeforeUnmount, watch } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { tw } from '@backmarket/utils/string/tw'
import { RevIllustration } from '@ds/components/Illustration'
import { openModal } from '@ds/components/ModalBase'
import { IconCheckWithSparkles } from '@ds/icons/IconCheckWithSparkles'
import { IconCrossSmall } from '@ds/icons/IconCrossSmall'

import { useStickyBarBottomOffset } from '~/composables/useStickyBarBottomOffset'
import { useCreateLinkFromPicker } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/composables/useCreateLinkFromPicker/useCreateLinkFromPicker'
import { useAddToCartOverlay } from '~/scopes/product-core/composables/stores/useAddToCartOverlay/useAddToCartOverlay'
import { useAddToCartVisibility } from '~/scopes/product-core/composables/stores/useAddToCartVisibility/useAddToCartVisibility'
import {
  CATEGORIES_WITH_UPGRADE,
  MODAL_NAMES,
} from '~/scopes/product-core/constants'
import { useShoppingAssistantFab } from '~/scopes/shopping-assistant/stores/useShoppingAssistantFab'

import {
  PILL_CLEARANCE_MARGIN_PX,
  UPGRADE_DELAY_MS,
} from './UpgradeForLess.constants'
import translations from './UpgradeForLess.translations'
import UpgradeModal from './components/UpgradeModal/UpgradeModal.vue'

const props = defineProps<{
  product: GetProductResponse
  pickers: GetPickersResponse
}>()

const emit = defineEmits<{
  (
    e: 'upgrade-accepted',
    pickerFields?: NonNullable<GetPickersResponse['upgrade']>['pickerFields'],
  ): void
}>()

const route = useRoute()
const i18n = useI18n()
const { trackClick } = useTracking()
const experiments = useExperiments()
const { createUpgradeLink } = useCreateLinkFromPicker()
const { shouldShowAddToCartOverlay } = useAddToCartOverlay()
const { checkVisibility } = useAddToCartVisibility()
const { isContainerAddToCartMobileViewport } = checkVisibility()

const { stickyBarBottomHeight } = useStickyBarBottomOffset()
const shoppingAssistantFab = useShoppingAssistantFab()

const pillStyle = computed(() => ({
  display: 'flex',
  bottom: `${stickyBarBottomHeight.value + PILL_CLEARANCE_MARGIN_PX}px`,
}))
const isLoading = useState('upgrade-for-less-is-loading', () => false)
const canUpgrade = useState('upgrade-for-less-can-upgrade', () => false)
const isUpgraded = useState('upgrade-for-less-is-upgraded', () => false)
const automaticModalOpening = useState(
  'upgrade-for-less-automatic-modal-opening',
  () => true,
)
const isDismissed = useState('upgrade-for-less-is-dismissed', () => false)
const firstLoad = useState('upgrade-for-less-first-load', () => true)
let timeoutUpgrade = null as ReturnType<typeof setTimeout> | null
const timeoutPosition = null as ReturnType<typeof setTimeout> | null

const isUpgradedLabelClassnames = tw`overflow-hidden after:absolute after:top-0 after:left-0 after:h-full after:w-full after:-translate-x-full after:content-['']`

const shouldDisplayUpgrade = computed(() => {
  return (
    !isLoading.value &&
    !isDismissed.value &&
    (isUpgraded.value || props.pickers.upgrade) &&
    experiments['experiment.ppUpgradeForLessV2'] === 'withUpgrade' &&
    CATEGORIES_WITH_UPGRADE.includes(props.product.tracking.categoryId)
  )
})

watch(
  shouldDisplayUpgrade,
  (isVisible) => {
    if (isVisible) {
      shoppingAssistantFab.hide()
    } else {
      shoppingAssistantFab.show()
    }
  },
  { immediate: true },
)

async function handleUpgradeAccepted() {
  canUpgrade.value = false
  isUpgraded.value = true

  emit('upgrade-accepted', props.pickers.upgrade?.pickerFields)

  await navigateTo(createUpgradeLink(props.pickers.upgrade))
}

function handlePillClick() {
  if (canUpgrade.value) {
    openModal(MODAL_NAMES.UPGRADE_FOR_LESS)
    trackClick({
      zone: 'upgrade_for_less_modal',
      value: 'open_modal',
    })
  }

  if (isUpgraded.value) {
    trackClick({
      zone: 'upgrade_for_less_modal',
      value: 'smart_upgrade_applied',
    })
  }
}

function handleClose() {
  isUpgraded.value = false
  canUpgrade.value = false
  isDismissed.value = true
}

onBeforeUnmount(() => {
  if (timeoutUpgrade) {
    clearTimeout(timeoutUpgrade)
  }
  if (timeoutPosition) {
    clearTimeout(timeoutPosition)
  }

  shoppingAssistantFab.show()
})

watch(isContainerAddToCartMobileViewport, () => {
  if (firstLoad.value && props.pickers.upgrade) {
    isLoading.value = true
    timeoutUpgrade = setTimeout(() => {
      firstLoad.value = false
      isLoading.value = false
      canUpgrade.value = true
    }, UPGRADE_DELAY_MS)
  }
})

// TO REFACTOR
// with same productID -> grade or other variants
// with different productID -> grade or other variants
watch(
  () => props.pickers,
  (newValue, oldValue) => {
    if (timeoutUpgrade) {
      clearTimeout(timeoutUpgrade)
    }

    // Detects if the user has upgraded the offer via the modal.
    // Keep the upgraded pill visible across all picker updates triggered by
    // the upgrade navigation, as long as no new upgrade becomes available.
    // The flag is only consumed once a new upgrade appears (fallthrough),
    // or when the user dismisses the pill via the close button.
    if (isUpgraded.value && !newValue?.upgrade && oldValue?.upgrade) {
      return
    }

    isUpgraded.value = false
    isDismissed.value = false

    // Detects first load and if we have a upgrade available for the first load
    // When we arrived on the page with no upgrade available we considere that the first load happened.
    // Otherwise it won't opened the modal automatically if the next picker has an upgrade.
    if (!oldValue && !newValue?.upgrade) {
      firstLoad.value = false

      return
    }

    // If no upgrade in new value -> don't do anything.
    if (!newValue?.upgrade) {
      return
    }

    isLoading.value = true
    canUpgrade.value = false
    timeoutUpgrade = setTimeout(() => {
      isLoading.value = false
      canUpgrade.value = true

      if (
        !firstLoad.value &&
        automaticModalOpening.value &&
        route.query.pickerClicked &&
        !shouldShowAddToCartOverlay.value
      ) {
        setTimeout(() => {
          automaticModalOpening.value = false
          firstLoad.value = false
          openModal(MODAL_NAMES.UPGRADE_FOR_LESS)
          trackClick({
            zone: 'upgrade_for_less_modal',
            value: 'open_modal_automatic',
          })
        }, 800)
      }
    }, UPGRADE_DELAY_MS)
  },
  { immediate: true },
)
</script>

<style module lang="css" scoped>
.slideInFromBottom {
  animation: slideUpFromBottom 500ms ease-out forwards;
}

@keyframes slideUpFromBottom {
  from {
    transform: translateX(-50%) translateY(100px);
  }

  to {
    transform: translateX(-50%) translateY(0);
  }
}

.animateShimmer::after {
  background-image: linear-gradient(
    100deg,
    rgba(255, 255, 255, 0) 20%,
    rgba(255, 255, 255, 0) 20%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0) 80%
  );
  animation: shimmer 2s 2s forwards;
}

@keyframes shimmer {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(100%);
  }
}
</style>
